export interface ReconciliationDateRange {
  start: string;
  end: string;
}

export interface ReconciliationFilters {
  reconFlag?: 'Y' | 'N';
  htsCodes?: string[];
  originCodes?: string[];
  manufacturerIds?: string[];
  entryLines?: string[];
}

// Backend API format for getTotalSumReconciliation filters
export interface ReconciliationFiltersInput {
  ReconFlag?: string;           // Note: Capital R
  hts_codes?: string[];         // Note: snake_case
  origin_codes?: string[];      // Note: snake_case
  manufacturer_ids?: string[];  // Note: snake_case
  entry_lines?: string[];       // Note: snake_case
}

// Transform frontend filters to backend API format
export const transformFiltersToBackend = (filters: ReconciliationFilters): ReconciliationFiltersInput => {
  const backendFilters: ReconciliationFiltersInput = {};
  
  if (filters.reconFlag) {
    backendFilters.ReconFlag = filters.reconFlag;
  }
  
  if (filters.htsCodes?.length) {
    backendFilters.hts_codes = filters.htsCodes;
  }
  
  if (filters.originCodes?.length) {
    backendFilters.origin_codes = filters.originCodes;
  }
  
  if (filters.manufacturerIds?.length) {
    backendFilters.manufacturer_ids = filters.manufacturerIds;
  }
  
  if (filters.entryLines?.length) {
    backendFilters.entry_lines = filters.entryLines;
  }
  
  return backendFilters;
};

export interface ValueAdjustmentData {
  adjustmentMode: 'percentage' | 'amount';
  percentage?: string; // "5%" - must include % symbol (when mode is percentage)
  amount?: number; // Dollar amount (when mode is amount)
  currency?: string; // Currency code like 'USD', 'EUR', etc. (when mode is amount)
  rateFactor?: string; // Calculated rate factor as percentage (when mode is amount)
  totalSum?: number; // Total sum from getTotalSumReconciliation (when mode is amount)
}

export type DutyAdjustmentOperation = 'code_change' | 'rate_adjustment';

export type CH99ChangeMode = 'replace_all' | 'selective';

export interface CH99Transformation {
  from: string; // Original CH99 code to find and replace
  to: string;   // New CH99 code to use as replacement
}

export interface DutyAdjustmentEntry {
  operation: DutyAdjustmentOperation;
  originalCode?: string;    // For code changes - the code to be replaced
  targetCode?: string;      // For code changes - the new code to use
  htsCode?: string;        // For rate adjustments - the code to modify
  newRate?: number;        // For rate adjustments - new duty rate (0-10 decimal)
  
  // CH99 code change specification (optional - for code_change operation only)
  ch99ChangeMode?: CH99ChangeMode; // 'replace_all' or 'selective'
  newCH99CodeList?: string[];      // For replace_all mode - complete new list of CH99 codes
  ch99Transformations?: CH99Transformation[]; // For selective mode - specific code transformations
}

export interface DutyRateAdjustmentData {
  adjustments: DutyAdjustmentEntry[];
}

export interface CombinedAdjustmentData {
  valueAdjustment: {
    adjustmentMode?: 'percentage' | 'amount';
    percentage: string; // e.g., "5%" - either directly entered or calculated from amount
    amount?: number; // Dollar amount (when mode is amount)
    currency?: string; // Currency code like 'USD' (when mode is amount)
    rateFactor?: string; // Calculated rate factor as percentage (when mode is amount)
    totalSum?: number; // Total sum from getTotalSumReconciliation (when mode is amount)
  };
  dutyRateAdjustment: {
    adjustments: DutyAdjustmentEntry[];
  };
}

export type AdjustmentType = 'value_percentage' | 'duty_rate_adjustment' | 'combined_value_and_duty';

export interface ReconciliationFormData {
  dateRange: ReconciliationDateRange;
  filters: ReconciliationFilters;
  adjustmentType: AdjustmentType;
  adjustmentData: ValueAdjustmentData | DutyRateAdjustmentData | CombinedAdjustmentData;
}

export interface ReconciliationInput {
  date_range: {
    start: string;
    end: string;
  };
  filters?: {
    ReconFlag?: string;
    hts_codes?: string[];
    origin_codes?: string[];
    manufacturer_ids?: string[];
    entry_lines?: string[];
  };
  adjustment: {
    type: AdjustmentType;
    data: ValueAdjustmentData | DutyRateAdjustmentData | CombinedAdjustmentData;
  };
  clientId: string;
  tenantId?: string;
}

export interface ReconciliationPreviewResponse {
  recordCount: number;
  adjustmentSummary: string;
  affectedFields: string[];
  estimatedProcessingTime: string;
}

export interface ReconciliationProcessResponse {
  fileId: string;
  progressBar: boolean;
  signedUrl?: string;
  adjustmentType: string;
}

export interface DownloadStatus {
  fileId: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  msg: string;
  url?: string;
  progress?: number;
}

export type ReconciliationStep = 'configure' | 'preview' | 'processing' | 'complete';

export interface ReconciliationState {
  formData: ReconciliationFormData;
  formErrors: Record<string, string[]>;
  currentStep: ReconciliationStep;
  preview: {
    loading: boolean;
    data?: ReconciliationPreviewResponse;
    error?: string;
  };
  processing: {
    loading: boolean;
    fileId?: string;
    progress?: number;
    status?: 'PENDING' | 'SUCCESS' | 'FAILED';
    downloadUrl?: string;
    error?: string;
  };
}

export interface DropdownOption {
  value: string;
  label: string;
  meta?: any;
}

export interface HTSCodeOption {
  value: string;
  label: string;
  type: 'hts' | 'ch99' | 'ch99Code' | 'ch99Code2';
}

// Legacy interface - replaced by DutyAdjustmentEntry
export interface HTSRateEntry {
  htsCode: string;
  rate: string;
}

export interface ValidationError {
  field: string;
  errors: string[];
}

// API Response Types
export interface TradeDataResponse {
  getAllTradeData: {
    data: Array<{
      htsNumberFull?: string;
      hts8Digit?: string;
    }>;
  };
}

export interface ProductsResponse {
  products: {
    data: Array<{
      countryOfOrigin?: string;
    }>;
  };
}

export interface ManufacturersResponse {
  dashboardVisibilityGetManufacturers: {
    data: Array<{
      manufacturer: string;
      manufacturerName?: string;
      tariffValue?: number;
      tariffDuty?: number;
      customValue?: number;
    }>;
    meta: {
      countDocs: number;
      pageNo: number;
      pageSize: number;
    };
  };
}

export interface HTSCodesResponse {
  getHTSCodesForDropdown: {
    data: HTSCodeOption[];
  };
}

// Helper functions for CH99 transformations
export const createEmptyTransformation = (): CH99Transformation => ({
  from: '',
  to: ''
});

export const validateCH99Code = (code: string): string | null => {
  if (!code || code.trim() === '') {
    return 'CH99 code is required';
  }
  
  // Accept both formats: 9999.99.99 or 99999999
  const dotFormat = /^99\d{2}\.\d{2}\.\d{2}$/;
  const plainFormat = /^99\d{6}$/;
  
  if (!dotFormat.test(code) && !plainFormat.test(code)) {
    return 'CH99 code must be 8 digits starting with 99 (with or without dots)';
  }
  
  return null;
};

export const validateTransformation = (transformation: CH99Transformation): string[] => {
  const errors: string[] = [];
  
  const fromError = validateCH99Code(transformation.from);
  if (fromError) {
    errors.push(`Original code: ${fromError}`);
  }
  
  const toError = validateCH99Code(transformation.to);
  if (toError) {
    errors.push(`Target code: ${toError}`);
  }
  
  return errors;
};